import type { Promisable } from "@core/foundation/promise";
import type { ReadonlyRecord } from "@core/foundation/types";
import { isNullish } from "@core/foundation/types";
import type { ZodType, z } from "@core/zod";

import type { NotificationHelperContract } from "./NotificationHelperContract.ts";

export type NotificationEvents = ReadonlyRecord<string, ZodType>;

export interface NotificationApi<Events extends NotificationEvents> {
  readonly events: Events;
}

export interface NotificationPublisher<Events extends NotificationEvents> {
  readonly publish: <Event extends keyof Events & string>(
    channel: string,
    event: Event,
    data: z.input<Events[Event]>,
  ) => Promisable<void>;
}

export function createPublisher<Events extends NotificationEvents>(
  api: NotificationApi<Events>,
  helper: NotificationHelperContract,
): NotificationPublisher<Events> {
  return {
    publish: async (channel, event, data) => {
      const schema: ZodType | undefined = api.events[event];

      if (isNullish(schema)) {
        throw new Error(`Unknown notification event "${event}"`);
      }

      const safeData = schema.decode(data);
      await helper.publish(channel, event, safeData);
    },
  };
}

export function notificationApi<const Events extends NotificationEvents>(
  events: Events,
): NotificationApi<Events> {
  return { events };
}
